import React, { useState } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { Plus, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { formatCurrency } from '../../lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Card, CardContent } from '../ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import { TaxonomyFieldsEditor } from './TaxonomyFieldsEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const STATUS_BADGE = {
  draft: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  submitted: 'bg-blue-100 text-blue-700',
  under_review: 'bg-indigo-100 text-indigo-700',
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
  paid: 'bg-teal-100 text-teal-700',
  verified: 'bg-green-100 text-green-800',
  recorded: 'bg-slate-100 text-slate-700',
  reconciled: 'bg-green-100 text-green-800',
};

export const StatusBadge = ({ status }) => (
  <Badge className={`capitalize ${STATUS_BADGE[status] || 'bg-slate-100 text-slate-700'}`} variant="outline">
    {(status || '').replace(/_/g, ' ')}
  </Badge>
);

const EMPTY_REQ = {
  category_group: '', subcategory: '', women_activity: '', details: {},
  amount: '', purpose: '', date_needed: '', vendor: '', funding_source: '', budget_code: '',
};

export const RequisitionsTab = ({ taxonomy, projects, onChanged }) => {
  const { user, hasPermission } = useAuth();
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_REQ);
  const [detail, setDetail] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [payForm, setPayForm] = useState(null); // {payment_method, payment_date, payment_reference, payee}

  const canApprove = hasPermission('requisitions', 'approve');
  const canReject = hasPermission('requisitions', 'reject');

  const load = React.useCallback(async () => {
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const r = await axios.get(`${API_URL}/api/finance/requisitions`, { params });
      setItems(r.data.items || []);
    } catch {
      toast.error('Failed to load requisitions');
    } finally {
      setLoaded(true);
    }
  }, [statusFilter]);

  React.useEffect(() => { load(); }, [load]);

  const submitCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/finance/requisitions`, {
        ...form,
        amount: parseFloat(form.amount),
        women_activity: form.women_activity || null,
        date_needed: form.date_needed || null,
        vendor: form.vendor || null,
        funding_source: form.funding_source || null,
        budget_code: form.budget_code || null,
      });
      toast.success('Requisition submitted for review');
      setCreateOpen(false);
      setForm(EMPTY_REQ);
      load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create requisition');
    }
  };

  const action = async (id, path, body = {}) => {
    try {
      const r = await axios.post(`${API_URL}/api/finance/requisitions/${id}/${path}`, body);
      toast.success(`Requisition ${path === 'pay' ? 'paid — expense created in Finance ledger' : path}`);
      setDetail(r.data.requisition || r.data);
      setPayForm(null);
      setRejectReason('');
      load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${path}`);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44" data-testid="req-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {['draft', 'submitted', 'under_review', 'approved', 'rejected', 'paid', 'verified'].map((s) => (
              <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, ' ')}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button onClick={() => setCreateOpen(true)} data-testid="new-requisition-btn">
          <Plus className="mr-2 h-4 w-4" /> New Requisition
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Req No.</TableHead>
                <TableHead>Subcategory</TableHead>
                <TableHead>Purpose</TableHead>
                <TableHead>Requester</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!loaded ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8">Loading…</TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No requisitions yet</TableCell></TableRow>
              ) : items.map((r) => (
                <TableRow key={r.id} className="table-row-hover">
                  <TableCell className="font-mono text-xs">{r.req_number}</TableCell>
                  <TableCell className="capitalize">{(r.subcategory || '').replace(/_/g, ' ')}</TableCell>
                  <TableCell className="max-w-[220px] truncate">{r.purpose}</TableCell>
                  <TableCell className="text-muted-foreground">{r.requester_name}</TableCell>
                  <TableCell><StatusBadge status={r.status} /></TableCell>
                  <TableCell className="text-right font-medium">{formatCurrency(r.amount)}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="outline" size="sm" onClick={() => setDetail(r)} data-testid={`view-req-${r.id}`}>
                      <Eye className="h-3.5 w-3.5 mr-1" /> View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">New Expense Requisition</DialogTitle>
            <DialogDescription>Request → Review → Authorization → Payment → Verification</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitCreate} className="space-y-4">
            <TaxonomyFieldsEditor taxonomy={taxonomy} idPrefix="req"
              value={{ category_group: form.category_group, subcategory: form.subcategory, women_activity: form.women_activity, details: form.details }}
              onChange={(v) => setForm((p) => ({ ...p, ...v }))} />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label>Amount Requested (UGX) *</Label>
                <Input type="number" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="req-amount" />
              </div>
              <div className="space-y-1.5">
                <Label>Date Needed</Label>
                <Input type="date" value={form.date_needed} onChange={(e) => setForm({ ...form, date_needed: e.target.value })} data-testid="req-date-needed" />
              </div>
              <div className="space-y-1.5">
                <Label>Budget Code</Label>
                <Input value={form.budget_code} onChange={(e) => setForm({ ...form, budget_code: e.target.value })} data-testid="req-budget-code" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Vendor / Provider</Label>
                <Input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} data-testid="req-vendor" />
              </div>
              <div className="space-y-1.5">
                <Label>Funding Source</Label>
                <Input value={form.funding_source} onChange={(e) => setForm({ ...form, funding_source: e.target.value })} data-testid="req-funding-source" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Purpose / Justification *</Label>
              <Textarea rows={2} required value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} data-testid="req-purpose" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="req-submit-btn">Submit Requisition</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) { setDetail(null); setPayForm(null); setRejectReason(''); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="req-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-2">
                  {detail.req_number} <StatusBadge status={detail.status} />
                </DialogTitle>
                <DialogDescription className="capitalize">
                  {(detail.category_group || '').replace(/_/g, ' ')} → {(detail.subcategory || '').replace(/_/g, ' ')}
                  {detail.women_activity ? ` · ${detail.women_activity.replace(/_/g, ' ')}` : ''}
                </DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-muted-foreground text-xs">Amount</span><p className="font-bold">{formatCurrency(detail.amount)}</p></div>
                <div><span className="text-muted-foreground text-xs">Requested by</span><p>{detail.requester_name}</p></div>
                <div className="col-span-2"><span className="text-muted-foreground text-xs">Purpose</span><p>{detail.purpose}</p></div>
                {detail.vendor && <div><span className="text-muted-foreground text-xs">Vendor</span><p>{detail.vendor}</p></div>}
                {detail.funding_source && <div><span className="text-muted-foreground text-xs">Funding source</span><p>{detail.funding_source}</p></div>}
                {detail.rejected_reason && <div className="col-span-2 text-red-600"><span className="text-xs">Rejection reason</span><p>{detail.rejected_reason}</p></div>}
              </div>
              {detail.details && Object.keys(detail.details).length > 0 && (
                <div className="border rounded-lg p-3 text-sm grid grid-cols-2 gap-2">
                  {Object.entries(detail.details).map(([k, v]) => (
                    <div key={k}><span className="text-muted-foreground text-xs capitalize">{k.replace(/_/g, ' ')}</span><p>{String(v)}</p></div>
                  ))}
                </div>
              )}
              {/* Workflow timeline */}
              <div className="space-y-1.5" data-testid="req-workflow-history">
                <p className="text-xs font-medium text-muted-foreground uppercase">Audit Trail</p>
                {(detail.workflow_history || []).map((h, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs border-l-2 border-primary/40 pl-2 py-0.5">
                    <span className="capitalize font-medium">{h.action.replace(/_/g, ' ')}</span>
                    <span className="text-muted-foreground">by {h.by_name}</span>
                    <span className="text-muted-foreground">{h.at ? format(new Date(h.at), 'PP p') : ''}</span>
                    {h.note && <span className="italic text-muted-foreground">— {h.note}</span>}
                  </div>
                ))}
              </div>

              {/* Payment form */}
              {payForm && (
                <div className="border rounded-lg p-3 space-y-3" data-testid="req-pay-form">
                  <p className="text-sm font-medium">Record Payment</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1"><Label className="text-xs">Payment Date *</Label>
                      <Input type="date" value={payForm.payment_date} onChange={(e) => setPayForm({ ...payForm, payment_date: e.target.value })} data-testid="req-pay-date" /></div>
                    <div className="space-y-1"><Label className="text-xs">Method *</Label>
                      <Select value={payForm.payment_method} onValueChange={(v) => setPayForm({ ...payForm, payment_method: v })}>
                        <SelectTrigger data-testid="req-pay-method"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          {['cash', 'mobile_money', 'bank_transfer', 'cheque', 'other'].map((m) => (
                            <SelectItem key={m} value={m} className="capitalize">{m.replace(/_/g, ' ')}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select></div>
                    <div className="space-y-1"><Label className="text-xs">Reference</Label>
                      <Input value={payForm.payment_reference} onChange={(e) => setPayForm({ ...payForm, payment_reference: e.target.value })} data-testid="req-pay-reference" /></div>
                    <div className="space-y-1"><Label className="text-xs">Payee</Label>
                      <Input value={payForm.payee} onChange={(e) => setPayForm({ ...payForm, payee: e.target.value })} data-testid="req-pay-payee" /></div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => setPayForm(null)}>Cancel</Button>
                    <Button size="sm" disabled={!payForm.payment_date || !payForm.payment_method}
                      onClick={() => action(detail.id, 'pay', payForm)} data-testid="req-pay-confirm">Confirm Payment</Button>
                  </div>
                </div>
              )}

              {/* Actions */}
              <DialogFooter className="flex-wrap gap-2">
                {detail.status === 'draft' && (
                  <Button size="sm" onClick={() => action(detail.id, 'submit')} data-testid="req-submit-action">Submit</Button>
                )}
                {detail.status === 'submitted' && canApprove && (
                  <Button size="sm" variant="outline" onClick={() => action(detail.id, 'review')} data-testid="req-review-action">Start Review</Button>
                )}
                {['submitted', 'under_review'].includes(detail.status) && canApprove && detail.requested_by !== user?.id && (
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => action(detail.id, 'approve')} data-testid="req-approve-action">Approve</Button>
                )}
                {['submitted', 'under_review'].includes(detail.status) && canReject && detail.requested_by !== user?.id && (
                  <div className="flex items-center gap-2">
                    <Input className="h-9 w-44" placeholder="Rejection reason" value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)} data-testid="req-reject-reason" />
                    <Button size="sm" variant="destructive" disabled={!rejectReason.trim()}
                      onClick={() => action(detail.id, 'reject', { reason: rejectReason })} data-testid="req-reject-action">Reject</Button>
                  </div>
                )}
                {detail.status === 'approved' && canApprove && !payForm && (
                  <Button size="sm" onClick={() => setPayForm({ payment_method: '', payment_date: format(new Date(), 'yyyy-MM-dd'), payment_reference: '', payee: detail.vendor || '' })} data-testid="req-record-payment-btn">Record Payment</Button>
                )}
                {detail.status === 'paid' && canApprove && (
                  <Button size="sm" className="bg-green-700 hover:bg-green-800" onClick={() => action(detail.id, 'verify')} data-testid="req-verify-action">Verify</Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
